import { de } from './de';
import { en } from './en';

type Widen<T> = { -readonly [K in keyof T]: T[K] extends string ? string : Widen<T[K]> };

/** Shape every locale must match exactly: German's keys, any string values. */
export type Messages = Widen<typeof de>;

export const locales = ['de', 'en'] as const;
export type Locale = (typeof locales)[number];
export const defaultLocale: Locale = 'de';

/** BCP 47 tags for Intl formatting (dates, numbers). */
export const intlLocale: Record<Locale, string> = { de: 'de-DE', en: 'en-GB' };

export const resources = {
  de: { translation: de },
  en: { translation: en }
} as const satisfies Record<Locale, { translation: Messages }>;

export const isLocale = (v: unknown): v is Locale => typeof v === 'string' && (locales as readonly string[]).includes(v);

export { de, en };

type Leaves<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string ? `${P}${K}` : Leaves<T[K], `${P}${K}.`>;
}[keyof T & string];

/** Every translation key as a dot path, e.g. 'mail.invite.subject'. */
export type MessageKey = Leaves<typeof de>;

/**
 * Minimal server-side translate (mails, exports). The SPA uses i18next with the
 * same catalogs; interpolation syntax is identical ({{name}}).
 */
export function translate(locale: Locale, key: MessageKey, vars: Record<string, string | number> = {}): string {
  const lookup = (tree: unknown): unknown =>
    key.split('.').reduce<unknown>((node, part) => (node && typeof node === 'object' ? (node as Record<string, unknown>)[part] : undefined), tree);
  const raw = lookup(resources[locale].translation) ?? lookup(resources[defaultLocale].translation);
  if (typeof raw !== 'string') return key;
  return raw.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name: string) => String(vars[name] ?? ''));
}
